import type { ColumnDef, SortingState } from '@tanstack/react-table';
import cx from 'clsx';
import dayjs from 'common/dayjs.ts';
import { fromQuerystring, GET, getRuntimeTimezone, POST, parseFormValues, toQuerystring } from 'common/helpers.ts';
import { isEqual } from 'es-toolkit';
import type { DivIcon, LatLngExpression } from 'leaflet';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { Option } from 'react-multi-select-component';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import type { FLEET_STATUS, Fleet } from 'types/Fleet.ts';
import { Button } from 'ui/component/Button.tsx';
import { IconBadge } from 'ui/component/IconBadge.tsx';
import { IDot, IFilters, IInfo, ISearch } from 'ui/component/Icons.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Tooltip } from 'ui/component/Tooltip.tsx';
import { Autocomplete } from 'ui/control/Autocomplete.tsx';
import { Input } from 'ui/control/Input.tsx';
import { SelectMulti } from 'ui/control/SelectMulti.tsx';
import { Toggle } from 'ui/control/Toggle.tsx';
import { usePageSizeInUrl } from 'ui/hook/usePageSizeInUrl.ts';
import { useRestoreScrollPositionOnBack } from 'ui/hook/useRestoreScrollPositionOnBack.ts';
import axios from '#admin/axios.ts';
import { Datetime } from '#admin/component/Datetime.tsx';
import { DriverLegsDialog } from '#admin/component/DriverLegsDialog.tsx';
import type { Marker } from '#admin/component/Map.tsx';
import { MarkerSmartphone } from '#admin/component/markers.tsx';
import { getSWRKeyForPage, Table } from '#admin/component/table/index.ts';

const locationFilterOptions = async (_options, filter): Promise<Option[]> => {
  if (filter.length < 3) return _options;

  // get language from the browser or use 'en' as default
  const language = navigator.language || 'en';

  const { data } = await axios.post('/fleet/locations', { search: filter, language });
  const options: Option[] = [];

  for (const item of data) {
    const label = `${item.name} (${item.country})`;
    const value = item.topLeftPosition.join() + ',' + item.bottomRightPosition.join();
    options.push({ label, value });
  }
  return options;
};

const MapComponent = React.lazy(() => import('../component/Map.tsx'));

const getColumns = (t: any, _onLegsDialogOpen: any): ColumnDef<Fleet>[] => {
  const columns: ColumnDef<Fleet>[] = [
    {
      accessorKey: 'name',
      header: t('labels.driver'),
      size: 150,
      cell: (info) => {
        const value = info.getValue<string>();
        return (
          <Link className="anchor anchor-novisit flex flex-row items-center gap-1" to={`/user/${info.row.original.user_id}`} data-table-no-row-events>
            <Tooltip text={value}>{value || '-'}</Tooltip>
          </Link>
        );
      },
    },
    {
      accessorKey: 'supplier_name',
      header: t('labels.supplier'),
      size: 150,
      cell: (info) => {
        const value = info.getValue<string>();
        return <Tooltip text={value}>{value || '-'}</Tooltip>;
      },
    },
    {
      accessorKey: 'location',
      header: t('labels.location'),
      size: 150,
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue<string>();
        return value || '-';
      },
    },
    {
      accessorKey: 'next_location',
      header: t('labels.nextStopLocation'),
      size: 150,
      enableSorting: false,
      cell: (info) => {
        const { leg_id, origin_address, origin_actual_datetime, origin_country, origin_city, destination_country, destination_city, destination_address } = info.row.original;
        const next_country = origin_actual_datetime ? destination_country : origin_country;
        const next_city = origin_actual_datetime ? destination_city : origin_city;
        const next_location = origin_actual_datetime ? destination_address : origin_address;

        if (!leg_id) return '-';
        return <Tooltip text={next_location}>{[next_city, next_country].filter(Boolean).join(',')}</Tooltip>;
      },
    },
    {
      accessorKey: 'next_eta',
      header: t('labels.nextStopEta'),
      size: 150,
      cell: (info) => {
        const { origin_tz, origin_expected_datetime, origin_actual_datetime, destination_tz, destination_expected_datetime } = info.row.original;
        const next_eta = origin_actual_datetime ? destination_expected_datetime : origin_expected_datetime;
        const next_tz = origin_actual_datetime ? destination_tz : origin_tz;

        return (
          <Datetime timezone={next_tz || getRuntimeTimezone()} truncateRegion={true} layout={'y'}>
            {next_eta}
          </Datetime>
        );
      },
    },
    {
      accessorKey: 'status',
      header: t('labels.status'),
      size: 140,
      cell: (info) => {
        const value = info.getValue<FLEET_STATUS>();
        return (
          <span className="flex flex-row items-center gap-1">
            <IDot size={18} className={value === 'live' ? 'fill-green' : 'fill-red'} />
            <span>{t(`status.${value}`)}</span>
          </span>
        );
      },
    },
    /*
    {
      id: 'actions',
      header: '',
      size: 40,
      meta: { className: '!p-0' },
      cell: (info) => {
        if (!info.row.original.leg_id) return null;
        return <FleetListItemMenu data={info.row.original} onReviewLegs={onLegsDialogOpen} />;
      },
    },
    */
  ];

  return columns;
};

type FormValues = {
  search: string;
  with_location: string;
  only_live: boolean | '';
};

const querystringParserOptions = { excludeKeys: ['size'] };

export const FleetManagement = () => {
  const { t } = useTranslation();
  const formRef = React.useRef<HTMLFormElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollRef = useRestoreScrollPositionOnBack('fleet-scroll');
  const [showFilters, setShowFilters] = React.useState<boolean>(false);
  const [sorters, setSorters] = React.useState<SortingState>([{ id: 'name', desc: true }]);
  const [highlightedRow, setHighlightedRow] = React.useState<Fleet | null>(null);
  const [legsDialog, setLegsDialog] = React.useState<null | { driver_id: string; driver_name: string }>(null);

  const defaultValues: FormValues = {
    search: '',
    with_location: '',
    only_live: false,
  };
  const defaultValuesWithSearchValues = { ...defaultValues, ...fromQuerystring(searchParams, querystringParserOptions) };
  const [values, setValues] = React.useState<typeof defaultValues>(defaultValuesWithSearchValues);

  const formContext = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: defaultValuesWithSearchValues,
  });
  const { handleSubmit, formState, reset, getValues, setValue } = formContext;

  const {
    data: { suppliers },
  } = useSWR(['/leg/utils/assigned-users'], GET, { fallbackData: { suppliers: [] } });

  const { data: rowsCount } = useSWR(formState.isValid ? ['/fleet/count', values, parseFormValues] : null, POST);
  const swr = useSWRInfinite(getSWRKeyForPage('/fleet/list', 25, values, sorters, parseFormValues), POST, {
    revalidateFirstPage: false,
  });
  const setSizeInUrl = usePageSizeInUrl(swr.setSize);
  const fleet: Fleet[] = React.useMemo(() => swr.data?.flat() || [], [swr]);

  const toggleFilters = () => setShowFilters(!showFilters);

  const resetFilters = () => {
    reset(defaultValues);
    setValue('with_location', '', { shouldDirty: false, shouldTouch: false });
    window.setTimeout(triggerSubmit, 50);
  };

  // wait next tick or URL will not be updated
  const triggerSubmit = React.useCallback(() => window.setTimeout(() => formRef.current?.requestSubmit(), 50), []);

  const onSubmit = async (values: FormValues) => {
    setSearchParams(toQuerystring(values, querystringParserOptions), { replace: true });
    setValues(values);
    await swr.mutate();
  };

  const onRowClick = (row: Fleet) => {
    if (!row.leg_id) return;
    setLegsDialog({ driver_id: row.user_id, driver_name: row.name });
  };
  const onRowMouseEnter = (row: Fleet) => setHighlightedRow(row);
  const onRowMouseLeave = () => setHighlightedRow(null);

  const onLegsDialogOpen = React.useCallback((driver_id: string, driver_name: string) => () => setLegsDialog({ driver_id, driver_name }), []);
  const onLegsDialogClose = React.useCallback(() => setLegsDialog(null), []);

  // we need active filters: if filter is of type array then we count if having least 1 selection, otherwise we count if being not nullish
  const filtersCount = Object.values(values).filter((value) => (Array.isArray(value) ? value.length > 0 : value !== '' && value !== null)).length;

  const columns = React.useMemo(() => getColumns(t, onLegsDialogOpen), [t, onLegsDialogOpen]);

  const markers = React.useMemo(() => {
    if (!fleet.length) return [];

    const markers: Marker[] = fleet
      .filter((f) => f.position)
      .map((item) => {
        const position: LatLngExpression = [...item.position].reverse() as LatLngExpression;
        const driverPosition_1h_ago = dayjs(item.timestamp).isBefore(dayjs().subtract(1, 'hour'));
        const markerColor = driverPosition_1h_ago ? 'bg-red' : 'bg-green';
        const markerIcon: DivIcon = MarkerSmartphone(cx(highlightedRow === item && 'scale-150', markerColor));

        const marker = {
          position,
          icon: markerIcon,
          content: (
            <div className="flex flex-col gap-2">
              <strong>
                {item.name} ({item.supplier_name || item.group_name})
              </strong>
              <span className="anchor" onClick={() => setLegsDialog({ driver_id: item.user_id, driver_name: item.name })}>
                {t('labels.seeLegs')}
              </span>
            </div>
          ),
        };
        return marker;
      });

    return markers;
  }, [fleet, highlightedRow, t]);

  // we can't rely only on formState.isDirty because it's true when defaultValues provided to useForm are changed, but we may have filters set by default provided in the URL
  const clearFiltersEnabled = formState.isDirty || !isEqual(defaultValues, getValues());

  return (
    <main className="view-container">
      {legsDialog && <DriverLegsDialog driver_id={legsDialog.driver_id} driver_name={legsDialog.driver_name} isOpen={!!legsDialog} onClose={onLegsDialogClose} />}

      <section className="flex flex-row place-content-between items-baseline">
        <h4>{t('navigation.fleetManagement')}</h4>
      </section>

      <div className={cx('mb-6 grid grid-cols-[1.5fr_1fr] gap-8')}>
        <section className="mt-4 h-fit bg-white p-8 pb-4">
          <FormProvider {...formContext}>
            <form id="search-form" ref={formRef} className="mb-4 flex flex-col gap-8" noValidate={true} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <div className="flex flex-row items-center gap-8">
                <Input
                  className="mr-auto w-[320px]"
                  name={'search'}
                  placeholder={t('placeholders.searchFleet')}
                  required={true}
                  autoFocus={true}
                  RightIcon={ISearch}
                  autoComplete={'off'}
                  debouncedAutoSubmitForm={'#search-form'}
                  debouncedAutoSubmitTimeout={500}
                />

                <Toggle className="relative" name={'only_live'} label={t('filters.onlyLive')} autoSubmitForm={'#search-form'}>
                  {/*Drivers with the location collect in the past 24h*/}
                  <Tooltip className="absolute top-1/2 -left-[18px] -translate-y-1/2" text={t('tooltips.onlyLive')}>
                    <IInfo className="fill-gray-700" />
                  </Tooltip>
                </Toggle>

                <IconBadge icon={<IFilters className="size-[24px] fill-gray-400" />} badge={filtersCount - 1 || null} onClick={toggleFilters} />
              </div>

              <div className={cx('filters-box', !showFilters && '!hidden')}>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-6">
                  <Autocomplete
                    className="col-span-2"
                    label={t('filters.driverLocation')}
                    name={'with_location'}
                    placeholderSearch={t('placeholders.searchCityOrCountry')}
                    filterOptions={locationFilterOptions}
                  />

                  <SelectMulti
                    className="col-span-1"
                    label={t('filters.supplier')}
                    name={'with_supplier'}
                    options={new Map(suppliers.map((supplier) => [supplier.id, supplier.name]))}
                    selectable={'one'}
                    closeOnChangedValue={true}
                    required={false}
                    hasClearAll={true}
                  />
                </div>

                <div className="flex flex-row gap-4">
                  <Button className="blue-outlined h-max min-w-max max-w-max p-1" type="button" onClick={toggleFilters}>
                    {t('actions.close')}
                  </Button>
                  <Button className="blue-outlined ml-auto min-w-max max-w-max" type="button" onClick={resetFilters} disabled={!clearFiltersEnabled}>
                    {t('actions.clearFilters')}
                  </Button>
                  <Button className="blue-outlined min-w-max max-w-max" disabled={showFilters && !formState.isDirty} type="submit">
                    {t('actions.apply')}
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>

          <Table
            scrollRef={scrollRef}
            className={cx('max-h-[calc(100vh-300px)]', !fleet.length && 'min-h-[300px]')}
            columns={columns}
            swr={{ ...swr, setSize: setSizeInUrl }}
            pagination={true}
            rowsCount={rowsCount}
            defaultSorting={sorters}
            onColumnSort={setSorters}
            estimateSize={50}
            overscan={50}
            onRowClick={onRowClick}
            onRowMouseEnter={onRowMouseEnter}
            onRowMouseLeave={onRowMouseLeave}
          />
        </section>

        <section className="relative mt-4 min-h-[calc(100vh-300px)]">
          {(swr.isValidating || markers === undefined) && <Spinner size={48} centered={true} />}
          {!swr.isValidating && markers !== undefined && (
            <React.Suspense fallback={<Spinner centered={true} />}>
              <MapComponent defaultZoom={4} markers={markers} />
            </React.Suspense>
          )}
        </section>
      </div>
    </main>
  );
};
